import { useCallback } from 'react'
import { useCookies } from 'react-cookie'
import useSWR from 'swr'
import { STORAGE_PREFIX } from '../../config'
import { APIDecoratorWithBaseURI } from '../../Service/api'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'
import { SalesReportFilterOptions, SalesReportRequest, SalesReportResponse } from './types'

export { downloadFromUrl, exportErrorMessage, useSalesReportExport } from './export'
export type { SalesReportExportFormat, SalesReportExportState, UseSalesReportExport } from './export'
export type {
  AppliedIdScope,
  CapacityConstraint,
  CourtesyMode,
  SalesReportCapacity,
  SalesReportFilterOptions,
  SalesReportGroup,
  SalesReportGroupBy,
  SalesReportPaymentTotal,
  SalesReportRequest,
  SalesReportResponse,
  SalesReportRow,
  SalesReportSeatMap,
  SalesReportSeatMapSector,
} from './types'

interface UseSalesReportFilters {
  filters: SalesReportFilterOptions | undefined
  isLoading: boolean
  error: unknown
  retry: () => void
}

export const useSalesReportFilters = (eventId: number): UseSalesReportFilters => {
  const url = `producer/analytics/salesReportFilters?eventId=${eventId}`
  const tokenKey = STORAGE_PREFIX + 'userJWT'
  const [cookies] = useCookies([tokenKey])
  const hasToken = LOCAL_EXECUTION || Boolean(cookies[tokenKey])
  const ready = hasToken && Number.isSafeInteger(eventId) && eventId > 0

  const fetcher = useCallback(async (): Promise<SalesReportFilterOptions> => {
    const response = await APIDecoratorWithBaseURI().get(url)
    return response.data.body as SalesReportFilterOptions
  }, [url])

  const { data, error, isLoading, mutate } = useSWR(ready ? url : null, fetcher)

  return {
    filters: data,
    isLoading: isLoading || !hasToken,
    error,
    retry: () => {
      void mutate()
    },
  }
}

interface UseSalesReport {
  report: SalesReportResponse | undefined
  isLoading: boolean
  isValidating: boolean
  error: unknown
  retry: () => void
}

export const useSalesReport = (eventId: number, request: SalesReportRequest | null): UseSalesReport => {
  const url = `producer/analytics/salesReport?eventId=${eventId}`
  const tokenKey = STORAGE_PREFIX + 'userJWT'
  const [cookies] = useCookies([tokenKey])
  const hasToken = LOCAL_EXECUTION || Boolean(cookies[tokenKey])
  const ready = hasToken && Number.isSafeInteger(eventId) && eventId > 0 && request !== null
  const body = request === null ? null : JSON.stringify(request)

  const fetcher = useCallback(async ([target, payload]: [string, string]): Promise<SalesReportResponse> => {
    const response = await APIDecoratorWithBaseURI().post(target, JSON.parse(payload))
    return response.data.body as SalesReportResponse
  }, [])

  const { data, error, isLoading, isValidating, mutate } = useSWR(ready ? [url, body] : null, fetcher, {
    keepPreviousData: true,
    revalidateOnFocus: false,
  })

  return {
    report: data,
    isLoading: isLoading || !hasToken,
    isValidating,
    error,
    retry: () => {
      void mutate()
    },
  }
}
